<script setup>
/**
 * SzMfaGate — Full-screen blocking overlay that forces MFA setup.
 * Renders on top of everything. Cannot be dismissed until MFA is activated.
 */
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { parseBoolean } from '@chatwoot/utils';
import mfaAPI from 'dashboard/api/mfa';
import MfaSetupWizard from 'dashboard/routes/dashboard/settings/profile/MfaSetupWizard.vue';

const store = useStore();

const visible = ref(false);
const checking = ref(true);
const showSetup = ref(false);
const provisioningUri = ref('');
const qrCodeUrl = ref('');
const secretKey = ref('');
const backupCodes = ref([]);
const setupWizardRef = ref(null);

const mfaFeatureEnabled = computed(() =>
  parseBoolean(window.chatwootConfig?.isMfaEnabled)
);

const currentUser = computed(() => store.getters.getCurrentUser);

async function checkMfaStatus() {
  if (!mfaFeatureEnabled.value) {
    checking.value = false;
    return;
  }
  try {
    const response = await mfaAPI.get();
    if (response.data.feature_available && !response.data.enabled) {
      visible.value = true;
    }
  } catch (e) {
    console.warn('[SzMfaGate] MFA check failed:', e);
  }
  checking.value = false;
}

async function startSetup() {
  try {
    const response = await mfaAPI.enable();
    provisioningUri.value = response.data.provisioning_uri || response.data.provisioning_url;
    if (response.data.qr_code_url) {
      qrCodeUrl.value = response.data.qr_code_url;
    }
    secretKey.value = response.data.secret;
    backupCodes.value = [];
    showSetup.value = true;
  } catch (e) {
    useAlert('Não foi possível iniciar a configuração do MFA.');
  }
}

async function verifyCode(code) {
  try {
    const response = await mfaAPI.verify(code);
    if (response.data.backup_codes) {
      backupCodes.value = response.data.backup_codes;
    }
    // Force wizard to backup step (since emit doesn't await)
    if (setupWizardRef.value) {
      setupWizardRef.value.setupStep = 'backup';
    }
    return true;
  } catch (e) {
    // Revert wizard to QR step on error
    if (setupWizardRef.value) {
      setupWizardRef.value.setupStep = 'qr';
      setupWizardRef.value.handleVerificationError(
        e.response?.data?.error || 'Código de verificação inválido'
      );
    }
    return false;
  }
}

function completeSetup() {
  visible.value = false;
  showSetup.value = false;
  useAlert('Autenticação de dois fatores ativada com sucesso!');
}

onMounted(() => {
  setTimeout(() => checkMfaStatus(), 1500);
});

watch(currentUser, (newVal, oldVal) => {
  if (newVal && newVal.id && (!oldVal || newVal.id !== oldVal.id)) {
    checkMfaStatus();
  }
});
</script>

<template>
  <Teleport to="body">
    <div v-if="visible" class="sz-mfa-gate">
      <div class="sz-mfa-gate-backdrop" />
      <div class="sz-mfa-gate-content">
        <!-- Header -->
        <div v-if="!showSetup" class="sz-mfa-gate-header">
          <div class="sz-mfa-gate-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <h2>Configure a autenticação de dois fatores</h2>
          <p>Por segurança, é obrigatório ativar a autenticação de dois fatores para acessar o sistema.</p>
        </div>

        <!-- Pre-setup -->
        <div v-if="!showSetup" class="sz-mfa-gate-start">
          <div class="sz-mfa-gate-features">
            <div class="sz-mfa-gate-feature">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="sz-check-icon"><path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/></svg>
              <span>Proteja sua conta contra acessos não autorizados</span>
            </div>
            <div class="sz-mfa-gate-feature">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="sz-check-icon"><path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/></svg>
              <span>Use Google Authenticator, Authy ou qualquer app TOTP</span>
            </div>
            <div class="sz-mfa-gate-feature">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="sz-check-icon"><path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/></svg>
              <span>Processo rápido — leva menos de 1 minuto</span>
            </div>
          </div>
          <button class="sz-mfa-gate-btn" @click="startSetup">
            Configurar agora
          </button>
        </div>

        <!-- Wizard header when active -->
        <div v-if="showSetup" class="sz-mfa-gate-wizard-header">
          <div class="sz-mfa-gate-icon sz-mfa-gate-icon-sm">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <h2>Configurar autenticação</h2>
        </div>

        <!-- MFA Setup Wizard -->
        <div v-if="showSetup" class="sz-mfa-gate-wizard">
          <MfaSetupWizard
            ref="setupWizardRef"
            :show-setup="true"
            :mfa-enabled="false"
            :provisioning-uri="provisioningUri"
            :secret-key="secretKey"
            :backup-codes="backupCodes"
            :qr-code-url-prop="qrCodeUrl"
            :hide-cancel="true"
            @verify="verifyCode"
            @complete="completeSetup"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style>
/* Not scoped — Teleport renders outside component tree, and we need
   .dark selectors to match body.dark > .sz-mfa-gate hierarchy */

.sz-mfa-gate {
  position: fixed;
  inset: 0;
  z-index: 999999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
}

.sz-mfa-gate-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(10px);
}

/* Light mode card */
.sz-mfa-gate-content {
  position: relative;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  background: #ffffff;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

/* Dark mode card — lighter bg for contrast */
.dark .sz-mfa-gate-content {
  background: #262629 !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* Override inner Chatwoot dark backgrounds to match our card */
.dark .sz-mfa-gate-content .bg-n-solid-1 {
  background-color: #2D2D30 !important;
}

.dark .sz-mfa-gate-content .outline-n-weak {
  outline-color: rgba(255, 255, 255, 0.1) !important;
}

.dark .sz-mfa-gate-content .border-n-slate-4,
.dark .sz-mfa-gate-content .border {
  border-color: rgba(255, 255, 255, 0.1) !important;
}

.dark .sz-mfa-gate-content .text-n-slate-12 {
  color: #F3F4F6 !important;
}

.dark .sz-mfa-gate-content .text-n-slate-11 {
  color: #9CA3AF !important;
}

.dark .sz-mfa-gate-content .text-n-slate-10 {
  color: #6B7280 !important;
}

/* Header */
.sz-mfa-gate-header {
  text-align: center;
  margin-bottom: 24px;
}

.sz-mfa-gate-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #EEF6F0;
  color: #388E3C;
  margin-bottom: 16px;
}

.dark .sz-mfa-gate-icon {
  background: rgba(76, 175, 80, 0.15) !important;
  color: #4CAF50 !important;
}

.sz-mfa-gate-icon-sm {
  width: 48px;
  height: 48px;
  margin-bottom: 12px;
}

.sz-mfa-gate-header h2,
.sz-mfa-gate-wizard-header h2 {
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 8px;
}

.dark .sz-mfa-gate-header h2,
.dark .sz-mfa-gate-wizard-header h2 {
  color: #F3F4F6 !important;
}

.sz-mfa-gate-header p {
  font-size: 14px;
  color: #6B7280;
  margin: 0;
  line-height: 1.5;
}

.dark .sz-mfa-gate-header p {
  color: #9CA3AF !important;
}

/* Wizard header */
.sz-mfa-gate-wizard-header {
  text-align: center;
  margin-bottom: 16px;
}

/* Features list */
.sz-mfa-gate-features {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
  padding: 16px;
  background: #F9FAFB;
  border-radius: 12px;
  border: 1px solid #E5E7EB;
}

.dark .sz-mfa-gate-features {
  background: rgba(255, 255, 255, 0.05) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

.sz-mfa-gate-feature {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #374151;
}

.dark .sz-mfa-gate-feature {
  color: #D1D5DB !important;
}

.sz-check-icon {
  flex-shrink: 0;
  color: #388E3C;
}

.dark .sz-check-icon {
  color: #4CAF50 !important;
}

/* CTA button */
.sz-mfa-gate-btn {
  width: 100%;
  padding: 12px;
  border-radius: 10px;
  border: none;
  background: #388E3C;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  font-family: inherit;
}

.sz-mfa-gate-btn:hover {
  background: #2E7D32;
}

.dark .sz-mfa-gate-btn {
  background: #4CAF50 !important;
}

.dark .sz-mfa-gate-btn:hover {
  background: #388E3C !important;
}

/* Wizard area */
.sz-mfa-gate-wizard {
  margin-top: -8px;
}

/* Input fields inside wizard — fix dark contrast */
.dark .sz-mfa-gate-wizard input {
  background: #1B1B1D !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
  color: #F3F4F6 !important;
}

.dark .sz-mfa-gate-wizard input::placeholder {
  color: #6B7280 !important;
}

/* Details/summary fix */
.dark .sz-mfa-gate-wizard details {
  border-color: rgba(255, 255, 255, 0.1) !important;
}

.dark .sz-mfa-gate-wizard summary:hover {
  background: rgba(255, 255, 255, 0.05) !important;
}

/* QR code container — white bg always for readability */
.sz-mfa-gate-wizard .bg-n-background {
  background: #ffffff !important;
}

/* Scrollbar */
.sz-mfa-gate-content::-webkit-scrollbar {
  width: 6px;
}
.sz-mfa-gate-content::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.12);
  border-radius: 3px;
}
.dark .sz-mfa-gate-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
}
</style>